import * as React from "react";
import Api from "./Api";
import "./SalesOrderPage.scss";
import AttachmentDlgCtl from "./AttachmentsDlg";
import CheckCtl from "./input/CheckCtl";
import CustomerCtl from "./CustomerCtl"
import DateCtl from "./input/DateCtl";
import LabelCtl from "./input/LabelCtl";
import LineItemsCtl from "./LineItemCtl";
import ModalCtl from "./input/ModalCtl";
import PaymentCtl from "./PaymentCtl";
import SelectCtl from "./input/SelectCtl";
import TextCtl from "./input/TextCtl";
import OwnerCtl, { OwnerRoles } from "./input/OwnerCtl";
import Constants from "./Constants"
import { isAccountingPersonnel } from "./Accounting";
import { groupSiteLicenseSubItems } from "./SiteLicense";
import { Guid } from "@testout/testout-commerce/core/Guid";
import { Campus } from "@testout/testout-commerce/models/labsim/Campus";
import { Company } from "@testout/testout-commerce/models/hubspot/Company";
import { Deal, LineItem } from "@testout/testout-commerce/models/hubspot/Deal";
import { Customer } from "@testout/testout-commerce/models/commerce/Customer";
import { Territory } from "@testout/testout-commerce/models/commerce/Territory";
import { ItemNumber } from "@testout/testout-commerce/models/commerce/ItemNumber";
import { ListItem } from "@testout/testout-commerce/models/ListItem";
import { Payment, CardBrand, PaymentTerms, PaymentMethod } from "@testout/testout-commerce/models/commerce/Payment";
import { PriceGroupCode } from "@testout/testout-commerce/models/commerce/PriceGroupCode";
import { Product } from "@testout/testout-commerce/models/commerce/Product";
import { ProductProperty, ProductPropertyKeys } from "@testout/testout-commerce/models/commerce/ProductProperty";
import { Quote } from "@testout/testout-commerce/models/commerce/Quote";
import { SalesBase } from "@testout/testout-commerce/models/commerce/SalesBase";
import { SalesItem } from "@testout/testout-commerce/models/commerce/SalesItem";
import { SalesOrder, SalesOrderStatus } from "@testout/testout-commerce/models/commerce/SalesOrder";
import { SalesReturn, SalesReturnType } from "@testout/testout-commerce/models/commerce/SalesReturn";
import Segment from "@testout/testout-commerce/models/Segment";

interface SalesOrderPageProps { }

interface SalesOrderPageState {
    initialized: boolean;
    inProgress: string;
    taxInProgress: boolean;
    errorMessage: string;
    salesOrder: SalesOrder;
    childrenCustomers: Object;
    showModal: boolean;
    showAttachmentModal: boolean;
    hsCompanyId?: number;
    modalHeader?: string;
    modalDescription?: string;
    modalSubmitButtonText?: string;
    exchangeProductNumbers?: string[];
    businessAddressCopiedOnce: boolean;
    priceLists?: ListItem[];
    territory?: Territory;
    employees: ListItem[];
}

export default class SalesOrderPage extends React.PureComponent<SalesOrderPageProps, SalesOrderPageState> {
    constructor(props: SalesOrderPageProps) {
        super(props);
        // Initialize minimal order values to reduce the screen flashing default values while the order loads
        const orderNumber = window.location.pathname.lastIndexOf("/") > 1
            ? window.location.pathname.slice(window.location.pathname.lastIndexOf("/") + 1)
            : ""
        this.state = {
            initialized: false, inProgress: "", errorMessage: "", taxInProgress: false, employees: [], showAttachmentModal: false,
            salesOrder: new SalesOrder({
                salesOrderId: new Guid(Guid.EMPTY), orderNumber: orderNumber,
                orderDate: new Date(),
                status: SalesOrderStatus.Open, useAutomatedFulfillment: true, priceCode: "0-15-AF"
            }), childrenCustomers: {}, showModal: false, businessAddressCopiedOnce: false
        };
    }

    private NEW_ORDER = ' -- New Order -- ';

    async componentDidMount() {
        // Parse the URL:
        // salesorder?dealId=xxxx     - Create a new order using data from HS deal ID xxxx (customer, line items filled in)
        // salesorder?companyId=xxxx  - Create a new order using data from HS company ID xxx (customer filled in)
        // salesorder?quoteNumber=xx  - Create a new order using data from quote number xx (customer, line items filled in)
        // salesorder/xxxx            - Bring up order xxxx from the Commerce database
        // salesorder                 - Bring up a blank order page

        const query = new URLSearchParams(window.location.search);

        if (query.has("dealId")) {
            this.setState({ initialized: true, errorMessage: 'Please create new orders from [CRM+](https://operations-comptia.crm.dynamics.com/).' });
            return;

            // Create an order from a HubSpot deal
            let dealId = query.get("dealId");
            try {
                // Load the deal line items into the order
                let deal = await Api.get<Deal>(`/api/deals/${dealId}`);
                if (deal) {
                    let salesOrder = new SalesOrder();
                    salesOrder.dealId = dealId || undefined;
                    deal.line_items.forEach((lineItem: LineItem) => {
                        var item = new SalesItem();
                        item.itemId = Guid.create();
                        item.productNumber = new ItemNumber(lineItem.hs_sku ?? "");
                        item.quantity = Number(lineItem.quantity ?? "1");
                        item.unitPrice = Number(lineItem.price ?? "0");
                        salesOrder.items.push(item);
                    });

                    groupSiteLicenseSubItems(salesOrder.items);

                    // Set the sequence of the remaining items
                    let sequence = 1;
                    salesOrder.items.filter(item => item.unitPrice > 0).forEach(item => item.sequence = sequence++);

                    if (deal.companies.length > 0)
                        this.getCustomerAndLoadPriceList(salesOrder, Number(deal.companies[0]?.hs_object_id), true, deal.companies[0]?.d365_account_number);
                    else
                        this.setState({ errorMessage: 'The HubSpot company for this deal was not found. Please close this window and try again.' });
                } else
                    this.setState({ errorMessage: 'The HubSpot deal was not found. Please close this window and try again.' });
            }
            catch (error: any) {
                this.setState({ errorMessage: `Error while loading the deal information: ${error.message}` });
            }
        }
        else if (query.has("companyId")) {
            this.setState({ initialized: true, errorMessage: 'Please create new orders from [CRM+](https://operations-comptia.crm.dynamics.com/).' });
            return;

            // Create an order from a HubSpot company
            let companyId = query.get("companyId");
            try {
                let company = await Api.get<Company>(`/api/companies/${companyId}`);
                if (company) {
                    let salesOrder = new SalesOrder();
                    this.getCustomerAndLoadPriceList(salesOrder, Number(company.hs_object_id), false, company.d365_account_number);
                } else
                    this.setState({ errorMessage: 'The HubSpot company was not found. Please close this window and try again.' });
            }
            catch (error: any) {
                this.setState({ errorMessage: `Error while loading the HubSpot company information: ${error.message}` });
            }
        }
        else if (query.has("quoteNumber")) {
            this.setState({ initialized: true, errorMessage: 'Please create new orders from [CRM+](https://operations-comptia.crm.dynamics.com/).' });
            return;

            // Create an order from a quote
            let quoteNumber = query.get("quoteNumber");
            try {
                let salesOrder = new SalesOrder();
                salesOrder.quoteNumber = quoteNumber || undefined;
                // Load the quote data into the order
                let quote = await Api.get<Quote>(`/api/quotes/${quoteNumber}`);
                if (quote) {
                    salesOrder = new SalesOrder(quote as unknown as SalesOrder);
                    // Give new primary key IDs to the sub-items (so we don't have database collisions)
                    salesOrder.payments.forEach(p => p.paymentId = Guid.create().toString());
                    for (let item of salesOrder.items) {
                        let saveId = item.itemId;
                        item.itemId = Guid.create();
                        // Reassign any parent IDs of sub-items who used the old parent ID of this item
                        for (let subItem of salesOrder.items.filter(i => i.parentItemId === saveId))
                            subItem.parentItemId = item.itemId;
                    }
                    salesOrder.status = SalesOrderStatus.Open;
                    if (salesOrder.shippingCustomer) {
                        let customerNumber = salesOrder.shippingCustomer.isStudent
                            ? (salesOrder.shippingCustomer.parentCustomerNumber ?? "")
                            : salesOrder.shippingCustomer.customerNumber;
                        let hsCompanyId = await Api.get(`/api/companies/hsCompanyId/${customerNumber}`);
                        this.setState({ hsCompanyId: hsCompanyId });
                    }
                    this.setCustomerTerritory(salesOrder);
                    this.showBundleSubItems(salesOrder);
                    salesOrder = await this.loadChildrenAndPriceList(salesOrder, true);
                    this.setState({ initialized: true, salesOrder: new SalesOrder(salesOrder) });
                } else
                    this.setState({ errorMessage: 'The sales order was not found. Please close this window and try again.' });
            }
            catch (error: any) {
                this.setState({ errorMessage: `Error while loading the sales order information: ${error.message}` });
            }
        }
        else if (window.location.pathname.lastIndexOf("/") > 1) {
            // Load a previous order
            let orderNumber = window.location.pathname.slice(window.location.pathname.lastIndexOf("/") + 1);
            try {
                let salesOrder = new SalesOrder(await Api.get(`/api/orders/${orderNumber}`));
                // Fix the UTC time that comes in incorrectly as a local time
                salesOrder.orderDate = new Date(salesOrder.orderDate.toLocaleString() + " GMT");
                if (salesOrder.orderNumber !== orderNumber) {
                    this.setState({ errorMessage: `Unable to find order ${orderNumber}`, initialized: true });
                    return;
                }
                if (salesOrder.shippingCustomer) {
                    let customerNumber = salesOrder.shippingCustomer.isStudent
                        ? (salesOrder.shippingCustomer.parentCustomerNumber ?? "")
                        : salesOrder.shippingCustomer.customerNumber;
                    let hsCompanyId = await Api.get(`/api/companies/hsCompanyId/${customerNumber}`);
                    this.setState({ hsCompanyId: hsCompanyId });
                }
                this.setCustomerTerritory(salesOrder);
                this.showBundleSubItems(salesOrder);
                salesOrder = await this.loadChildrenAndPriceList(salesOrder, true);
                this.setState({ salesOrder: new SalesOrder(salesOrder) });
                if (salesOrder.status === 0)
                    this.setState({ errorMessage: 'Please create new orders from [CRM+](https://operations-comptia.crm.dynamics.com/).' });
            }
            catch (error: any) {
                this.setState({ errorMessage: `Error while loading the order: ${error.message}` });
            }
        }
        else {
            this.setState({ initialized: true, errorMessage: 'Please create new orders from [CRM+](https://operations-comptia.crm.dynamics.com/).' });
            return;
        }

        // Save the employee list for lookups
        let employees = await Api.get<ListItem[]>('/api/employees/list-items');
        this.setState({ employees: employees });

        this.setState({ initialized: true });
    }

    showBundleSubItems = (salesOrder: SalesOrder) => {
        // In the line items, show site license sub-items if they are there
        for (let item of salesOrder.items) {
            let parentId = item.parentItemId;
            if (parentId) {
                let parents = salesOrder.items.filter(i => i.itemId === parentId);
                if (parents?.length === 1)
                    parents[0].showBundle = true;
            }
        }
    }

    async getCustomerAndLoadPriceList(salesOrder: SalesOrder, hsCompanyId: number, checkOverride: boolean, customerNumber?: string) {
        try {
            // Load the customer from the database if it is new to this order
            let customer = salesOrder.shippingCustomer;
            if ((customerNumber ?? "") !== "") {
                customer = await Api.get<Customer>(`/api/customers/${customerNumber}`);
                if (!customer)
                    this.setState({ errorMessage: `Unable to find customer number ${customerNumber} in the Commerce database` });
                // Clear unneeded field
                customer.parentCustomerNumber = "";
            }

            salesOrder = await this.setSalesOrderCustomerAndLoadPriceList(customer, salesOrder, hsCompanyId, checkOverride);
            this.setState({ salesOrder: salesOrder });
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while loading the customer information: ${error.message}` });
        }
    }

    async setSalesOrderCustomerAndLoadPriceList(customer: Customer, salesOrder: SalesOrder, hsCompanyId: number = 0, checkOverride: boolean): Promise<SalesOrder> {
        salesOrder.shippingCustomer = customer;
        salesOrder.priceGroupId = customer.priceGroupId;
        salesOrder.priceId = customer.priceId;
        salesOrder.paymentTerms = customer.paymentTerms;
        salesOrder.useAutomatedFulfillment = customer.useAutomatedFulfillment;
        if (!salesOrder.salespersonCode)
            salesOrder.salespersonCode = customer.salespersonCode;
        // If the customer is assigned a different billing customer, then load it
        if (customer.billingCustomerNumber && customer.billingCustomerNumber !== customer.customerNumber) {
            salesOrder.billingSameAsShipping = false;
            try {
                salesOrder.billingCustomer = await Api.get<Customer>(`/api/customers/${customer.billingCustomerNumber}`);
                if (!salesOrder.billingCustomer)
                    this.setState({ errorMessage: `Unable to find billing customer number ${customer.billingCustomerNumber} in the Commerce database` });
                // Clear unneeded fields
                salesOrder.billingCustomer.billingCustomerNumber = "";
                salesOrder.billingCustomer.parentCustomerNumber = "";
            }
            catch (error: any) {
                this.setState({ errorMessage: `Error while loading the billing customer ${customer.billingCustomerNumber}: ${error.message}` });
            }
        } else {
            salesOrder.billingSameAsShipping = true;
        }
        customer.billingCustomerNumber = "";     // Clear unneeded field
        if (hsCompanyId !== 0)
            this.setState({ hsCompanyId: hsCompanyId });

        await this.setCustomerTerritory(salesOrder);
        return await this.loadChildrenAndPriceList(salesOrder, checkOverride);
    }

    async setCustomerTerritory(salesOrder: SalesOrder) {
        let territory: Territory;
        if (salesOrder.territoryCode)
            territory = await Api.getObject(Territory, `/api/territories/${salesOrder.territoryCode}`);
        else if (salesOrder.shippingCustomer.territoryCode)
            territory = await Api.getObject(Territory, `/api/territories/${salesOrder.shippingCustomer.territoryCode}`);
        else {
            let customerNumber = salesOrder.shippingCustomer.isStudent
                ? (salesOrder.shippingCustomer.parentCustomerNumber ?? "")
                : salesOrder.shippingCustomer.customerNumber;
            let customer = await Api.get<Customer>(`/api/customers/${customerNumber}`);
            if (!customer)
                return;
            if (customer.territoryCode)
                territory = await Api.getObject(Territory, `/api/territories/${customer.territoryCode}`);
            else
                territory = await Api.getObject(Territory, `/api/customers/territories?name=${encodeURIComponent(customer.name)}&segment=${customer.segment}&province=${customer.address.postal.stateOrProvince}&country=${customer.address.postal.countryCode}`);
        }
        if (territory) {
            // Default to the AE as the sales person unless the CSM or the MCS have been selected
            if (!(salesOrder.salespersonCode === territory.roles[OwnerRoles.CSM] || salesOrder.salespersonCode === territory.roles[OwnerRoles.MCS]))
                salesOrder.salespersonCode = territory.roles[OwnerRoles.AE];
            salesOrder.territoryCode = territory.territoryCode;
        }
        this.setState({ territory: territory });
    }

    async loadChildrenAndPriceList(salesOrder: SalesOrder, checkOverride: boolean): Promise<SalesOrder> {
        await this.loadChildrenCustomersList(salesOrder.shippingCustomer);
        salesOrder = await this.loadPriceList(salesOrder);
        if (checkOverride) {
            // Set the override to true if the price is not the default (unless it is a price-locked product)
            salesOrder.items.filter(item => item._unitPrice !== item.pricing?.unitPrice &&
                !salesOrder.pricing?.priceList?.products.find((p: Product) => p.productNumber === item.productNumber?.toString())?.properties?.some((pp: ProductProperty) => pp.key === ProductPropertyKeys.PriceLocked)
            ).forEach(item => { item.overridePricing = true });
            // If the billing shipping is already not same as delivery, don't copy it again
            if (!salesOrder.billingSameAsShipping)
                this.setState({ businessAddressCopiedOnce: true });
        }
        return salesOrder;
    }

    async loadPriceList(salesOrder: SalesOrder): Promise<SalesOrder> {
        try {
            salesOrder.pricing = await Api.getObject(PriceGroupCode, `/api/prices/pricegroupcodebyid?priceGroupId=${salesOrder?.priceGroupId ?? 0}&priceId=${salesOrder?.priceId ?? 15}`);

            // Re-assign unit codes from this price list to any existing sales items (if we can find matches)
            salesOrder.items.forEach(item => { item.unitCode = salesOrder.pricing?.priceList?.prices.find(p => p.productNumber === item.productNumber.toString())?.unitCode ?? item.unitCode });

            // Load the price lists possible for this price group ID
            this.setState({ priceLists: await Api.get<ListItem[]>(`/api/prices/grouppricelists/${salesOrder?.priceGroupId ?? 0}`) });
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while loading the price list: ${error.message}` });
        }
        return salesOrder;
    }

    async loadChildrenCustomersList(customer: Customer) {
        let customerNumber = customer.isStudent ? (customer.parentCustomerNumber ?? "") : customer.customerNumber;
        if (customerNumber === "")
            return;
        let errorMessage = "";
        try {
            let childSchools = await Api.get(`/api/customers/${customerNumber}/children`);
            if (Object.entries(childSchools).length > 0) {
                Object.keys(childSchools).forEach(key => {   // For some reason the first 4 characters of the customer number are lower case. Force it to upper case.
                    var upper = key.toUpperCase();
                    if (upper !== key) {
                        childSchools[key.toUpperCase()] = childSchools[key];
                        delete childSchools[key];
                    }
                });
                this.setState({ childrenCustomers: { [Constants.MAIN_SCHOOL]: [customer.name], ...childSchools } });
            } else {
                // Clear any prior values
                this.setState({ childrenCustomers: {} });
            }
        }
        catch (error: any) {
            errorMessage = `Error while loading the sub-schools: ${error.message} `;
        }

        // Add up a warning error message if the customer's credit is on hold
        if (customer.creditOnHold)
            errorMessage += "WARNING! This customer's credit is ON HOLD";

        this.setState({ errorMessage: errorMessage });
    }

    // Save purchase order changes to the order
    onPurchaseOrderNumberChange = (value: string) => {
        let salesOrder = new SalesOrder(this.state.salesOrder);
        salesOrder.purchaseOrderNumber = value;
        this.setState({ salesOrder: salesOrder });
    }

    // Save order date changes to the order
    onOrderDateChange = (value?: Date) => {
        let salesOrder = new SalesOrder(this.state.salesOrder);
        salesOrder.orderDate = value ?? new Date();
        this.setState({ salesOrder: salesOrder });
    }

    // Save any changes to the shipping customer info
    onShippingCustomerChange = async (customer: Customer) => {
        let salesOrder = new SalesOrder(this.state.salesOrder);
        let recalculateTaxes = (salesOrder.shippingCustomer?.address.postal.stateOrProvince !== customer.address.postal.stateOrProvince ||
            salesOrder.shippingCustomer?.address.postal.postalCode !== customer.address.postal.postalCode);
        salesOrder.billingCustomer = salesOrder.billingSameAsShipping ? customer : salesOrder.billingCustomer;

        // If a new customer was selected, load the school info and any sub-schools for this customer
        if (salesOrder.shippingCustomer.customerNumber !== customer.customerNumber) {
            // Force the sales person and territory to be re-loaded from the customer's info
            salesOrder.salespersonCode = undefined;
            salesOrder.territoryCode = undefined;
            for (let item of salesOrder.items)  // Clear any prior sub-schools
                item.customerNumber = Constants.MAIN_SCHOOL;
            salesOrder = await this.setSalesOrderCustomerAndLoadPriceList(customer, salesOrder, 0, false);
        }
        else {
            salesOrder.shippingCustomer = customer;
        }

        // Recalculate the taxes if the state/province or zipcode has changed
        if (recalculateTaxes && salesOrder.items.length > 0)
            this.checkTheTaxes(salesOrder);
        this.setState({ salesOrder: salesOrder });
    }

    // Save any changes to the billing same as shipping check box
    onBillingSameAsShippingChange = (value: boolean) => {
        let salesOrder = new SalesOrder(this.state.salesOrder);
        // If this is the first time this checkbox has been turned off, copy the data from shipping to billing
        if (this.state.businessAddressCopiedOnce === false && value === false) {
            salesOrder.billingCustomer = JSON.parse(JSON.stringify(salesOrder.shippingCustomer));
            this.setState({ businessAddressCopiedOnce: true });
        }
        salesOrder.billingSameAsShipping = value;
        this.setState({ salesOrder: salesOrder });
    }

    // Save any changes to the billing customer info
    onBillingCustomerChange = (customer: Customer) => {
        let salesOrder = new SalesOrder(this.state.salesOrder);
        salesOrder.billingCustomer = customer;
        this.setState({ salesOrder: salesOrder });
    }

    onAutomatedFulfillmentChange = (value: boolean) => {
        let salesOrder = new SalesOrder(this.state.salesOrder);
        salesOrder.useAutomatedFulfillment = value;
        this.setState({ salesOrder: salesOrder });
    }

    onPriceIdChange = async (value: string) => {
        // Load the newly-selected price list into the current order
        const priceId = parseInt(value);
        let salesOrder = new SalesOrder(this.state.salesOrder);
        salesOrder.priceId = priceId;
        salesOrder = await this.loadPriceList(salesOrder);

        // If the order has items, re-check the taxes since the prices of the order's items may have changed
        if (salesOrder.items.length > 0)
            this.checkTheTaxes(salesOrder);

        this.setState({ salesOrder: salesOrder });
    }

    // Save any changes to the order payment info
    onPaymentChange = (paymentTerms: string, payment: Payment | undefined) => {
        let salesOrder = new SalesOrder(this.state.salesOrder);
        salesOrder.paymentTerms = paymentTerms;
        salesOrder.payments = (payment) ? [payment] : new Array<Payment>();
        this.setState({ salesOrder: salesOrder });
    }

    // Save any changes to the order comments
    onCommentsChange = (value: string) => {
        let salesOrder = new SalesOrder(this.state.salesOrder);
        salesOrder.comments = value;
        this.setState({ salesOrder: salesOrder });
    }

    onSalespersonChange = (value: string) => {
        let salesOrder = new SalesOrder(this.state.salesOrder);
        salesOrder.salespersonCode = value;
        this.setState({ salesOrder: salesOrder });
    }

    // The selected customer has changed, load the newly-selected customer info
    onItemCustomerSelect = async (value: string, exact?: boolean): Promise<ListItem[]> => {
        try {
            this.setState({ errorMessage: "" });        // Clear any prior errors
            return exact
                ? [await Api.get<ListItem>(`/api/customers/${value}/list-item`)]
                : await Api.get<ListItem[]>(`/api/customers/list-items?search=${encodeURIComponent(value)}`);
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while loading the customer information: ${error.message}` });
            return [{ name: "", value: "" }];
        }
    }

    setStateSalesOrder = (salesBase: SalesBase) => {
        let salesOrder = new SalesOrder({
            ...salesBase,
            salesOrderId: this.state.salesOrder.salesOrderId, status: this.state.salesOrder.status,
            orderNumber: this.state.salesOrder.orderNumber, orderDate: this.state.salesOrder.orderDate
        });

        // Manually copy calculated fields
        salesOrder.currencyCode = salesBase.currencyCode;
        salesOrder.priceCode = salesBase.priceCode;
        salesOrder.priceGroupId = salesBase.priceGroupId;
        salesOrder.priceId = salesBase.priceId;
        salesOrder.billingSameAsShipping = salesBase.billingSameAsShipping;
        salesOrder.billingCustomer = salesBase.billingCustomer;
        salesOrder.shippingCustomer = salesBase.shippingCustomer;

        this.setState({ salesOrder: salesOrder });
    }

    showError = (message: string) => this.setState({ errorMessage: message });

    checkingTaxes = (inProgress: boolean) => this.setState({ taxInProgress: inProgress });

    private _taxTimer: number | undefined = undefined;
    checkTheTaxes = async (salesOrder: SalesOrder) => {
        if (this._taxTimer)
            window.clearTimeout(this._taxTimer);
        this._taxTimer = window.setTimeout(async () => {
            this._taxTimer = undefined;
            this.checkingTaxes(true);
            let salesBase = salesOrder;
            try {
                // Use Commerce Server to contact Avalara for the tax amount(s) of the current order
                let taxedOrder = await Api.post<SalesOrder>("/api/orders/gettaxes", salesBase);

                // Copy the tax amounts we got from Avalara
                salesBase.taxSchedule = taxedOrder.taxSchedule;
                salesBase.freightTaxAmount = taxedOrder.freightTaxAmount;
                for (var item of salesBase.items) {
                    let itemId = item.itemId.toString();
                    let taxedItem = taxedOrder.items.filter((taxItem: SalesItem) => taxItem.itemId.toString() === itemId);
                    if (taxedItem !== undefined && taxedItem[0] !== undefined)
                        item.taxAmount = taxedItem[0].taxAmount;
                }
                salesBase.shippingCustomer.address.postal.postalCode = this.state.salesOrder.shippingCustomer.address.postal.postalCode;    // Get any user input changes while calculating taxes
                this.setStateSalesOrder(salesBase);
                this.checkingTaxes(false);
            }
            catch (error: any) {
                this.showError(`Taxes were not calculated correctly: ${error.message}`);
                this.checkingTaxes(false);
            }
        }, 500);
    }

    // Save the current order to the Commerce Server
    saveSalesOrder = async () => {
        this.setState({ errorMessage: '' });    // Clear any prior error messages
        let salesOrder = this.cleanUpOrder(new SalesOrder(this.state.salesOrder));
        if (salesOrder == null)
            return;

        // Save the order
        try {
            this.setState({ errorMessage: "", inProgress: "Order is being saved..." });        // Clear any prior errors and set that we are saving
            let orderNumber = await Api.post<string>("/api/orders/savedraft", salesOrder);
            salesOrder.orderNumber = orderNumber;
            this.setState({ salesOrder: salesOrder, inProgress: "" });
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while saving the draft sales order: ${error.message}`, inProgress: "" });
            return;
        }
    }

    private cleanUpOrder = (salesOrder: SalesOrder): SalesOrder | null => {
        // Check for valid address information
        if ((salesOrder.shippingCustomer?.address?.postal?.countryCode ?? "") === "") {
            this.setState({ errorMessage: 'Order does not have a shipping Country address, which is needed for tax calculations' });
            return null;
        }
        if ((salesOrder.shippingCustomer?.address?.postal?.countryCode ?? "") === "USA" &&
            (salesOrder.shippingCustomer?.address?.postal?.stateOrProvince ?? "") === "") {
            this.setState({ errorMessage: 'Order does not have a shipping State address, which is needed for tax calculations' });
            return null;
        }
        if ((salesOrder.billingCustomer?.address?.postal?.countryCode ?? "") === "") {
            this.setState({ errorMessage: 'Order does not have a billing Country address, which is needed for tax calculations' });
            return null;
        }
        if ((salesOrder.billingCustomer?.address?.postal?.countryCode ?? "") === "USA" &&
            (salesOrder.billingCustomer?.address?.postal?.stateOrProvince ?? "") === "") {
            this.setState({ errorMessage: 'Order does not have a billing State address, which is needed for tax calculations' });
            return null;
        }

        // Set any required fields that are blank
        if (salesOrder.shippingMethods?.length !== 0)
            salesOrder.shippingMethods = ["Internet Delivery"];
        if (salesOrder.salesOrderId.value === Guid.EMPTY)
            salesOrder.salesOrderId = Guid.create();
        if (!salesOrder.salespersonCode || salesOrder.salespersonCode === "")
            salesOrder.salespersonCode = salesOrder.shippingCustomer.salespersonCode;
        if (!salesOrder.territoryCode || salesOrder.territoryCode === "")
            salesOrder.territoryCode = salesOrder.shippingCustomer.territoryCode;

        // Set fields for PayPal so that the order will go through
        if ((salesOrder.payments[0]?.cardBrand ?? CardBrand.Visa.toString()) === CardBrand.PayPal.toString()) {
            salesOrder.payments[0].cardSuffix = "0000";
            salesOrder.payments[0].cardExpirationMMYY = "0200";
        }

        // Fix the default customer number to be un-set
        salesOrder.items.filter(item => item.customerNumber === Constants.MAIN_SCHOOL).forEach(item => item.customerNumber = undefined);

        return salesOrder;
    }

    // Submit the current order to the Commerce Server for fulfillment
    submitSalesOrder = async () => {
        this.setState({ errorMessage: '' });    // Clear any prior error messages
        let salesOrder = this.cleanUpOrder(new SalesOrder(this.state.salesOrder));
        if (salesOrder == null)
            return;

        // Check the order for missing values
        if ((salesOrder.shippingCustomer?.address?.email ?? "") === "") {
            this.setState({ errorMessage: 'Order does not have a shipping email address' });
            return;
        }
        if ((salesOrder.billingCustomer?.address?.email ?? "") === "") {
            this.setState({ errorMessage: 'Order does not have a billing email address' });
            return;
        }
        if ((salesOrder.purchaseOrderNumber ?? "") === "" && salesOrder.paymentTerms !== PaymentTerms.Cash.toString()) {
            this.setState({ errorMessage: 'P.O. number is required if order is paid by terms (Net 15, Net 30, etc.)' });
            return;
        }
        if (salesOrder.paymentTerms === PaymentTerms.Cash.toString()) {
            if (!salesOrder.payments || salesOrder.payments.length === 0) {
                this.setState({ errorMessage: 'Payment amount must be non-zero' });
                return;
            }
            if (salesOrder.payments[0].amount === 0) {
                this.setState({ errorMessage: `Payment amount must be non-zero for a payment method of ${PaymentMethod[salesOrder.payments[0].method as PaymentMethod]}` });
                return;
            }
            if (salesOrder.payments[0].method === PaymentMethod.Card) {
                if ((salesOrder.payments[0]?.cardSuffix ?? "") === "") {
                    this.setState({ errorMessage: 'Credit card number is required' });
                    return;
                }
                if (!salesOrder.payments[0]?.cardExpirationDate) {
                    this.setState({ errorMessage: 'Credit card expiration date is required' });
                    return;
                }
                if ((salesOrder.payments[0]?.cardTransactionCode ?? "") === "") {
                    this.setState({ errorMessage: 'Credit card transaction code is required' });
                    return;
                }
            }
            if (salesOrder.payments[0].method === PaymentMethod.Check && (salesOrder.payments[0]?.checkNumber ?? "") === "") {
                this.setState({ errorMessage: 'Check number is required' });
                return;
            }
        }

        let mainCustomerNumber = salesOrder.shippingCustomer.isStudent
            ? (salesOrder.shippingCustomer.parentCustomerNumber ?? "")
            : salesOrder.shippingCustomer.customerNumber;

        // If this order has sub-schools, get a list of all unique sub-schools to check on this order
        let checkCustomerNumbers: string[] = [];
        if (!salesOrder.shippingCustomer.isStudent && salesOrder.items.some(i => i.customerNumber !== "")) {
            for (let item of salesOrder.items) {
                // If this is a site license, ignore it
                if (item.subItems.length > 0)
                    continue;
                // If the customer number is undefined, use the main customer number
                let customerNumber = item.customerNumber ?? mainCustomerNumber;
                // If we haven't added this customer number yet, add it to the list
                if (!checkCustomerNumbers.includes(customerNumber))
                    checkCustomerNumbers.push(customerNumber);
            }
        }
        // Check the main customer number if no sub schools were added to the list to check
        if (checkCustomerNumbers.length === 0)
            checkCustomerNumbers.push(mainCustomerNumber);

        let customerNames = "";
        let destinationCampus = "";
        // Check each unique customer to see if a LabSim campus has been set
        for (let checkCustomerNumber of checkCustomerNumbers) {
            var ret = await this.checkLabSimCampusAssigned(checkCustomerNumber, customerNames, destinationCampus);
            if (ret.error)
                return;
            customerNames = ret.customerNames || "";
            destinationCampus = ret.destinationCampus || "";
        }

        if (customerNames !== "" && salesOrder.useAutomatedFulfillment) {
            this.setState({ errorMessage: `The LabSim campus has not been set for ${customerNames}. Please save this order, close this window and set the LabSim campus in the Customer card.` });
            return;
        }

        // Make sure that this is the correct destination for the order. The user will submit the order if it is correct, cancel if not.
        this.setState({
            showModal: true,
            modalHeader: "Submit order?",
            modalDescription: `This order will be sent to the following LabSim campus${destinationCampus.indexOf(', ') !== -1 ? "es" : ""}: ${destinationCampus}. Is that correct?`,
            modalSubmitButtonText: "Submit",
            salesOrder: salesOrder
        });
    }

    checkLabSimCampusAssigned = async (customerNumber: string, customerNames: string, destinationCampus: string) => {
        var checkCampus = true;
        // Get the customer info for this customerNumber (and, in doing so, check that the customerNumber is valid)
        var customer: Customer;
        try {
            customer = await Api.get<Customer>(`/api/customers/${customerNumber}`);
            if (!customer?.entityId || customer.entityId === Guid.createEmpty()) {
                // If a LabSim campus is not assigned, add the customer name to the list of unassigned campuses
                if (customerNames !== "")
                    customerNames += ", ";
                customerNames += customer.name;
                checkCampus = false;
            }
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while retrieving the customer information for ${customerNumber}: ${error.message}` });
            return { error: true };
        }

        if (checkCampus) {
            // Get the campus name (and thereby check if this is a valid campus guid)
            try {
                let campus = await Api.get<Campus>(`/api/campuses/${customer.entityId}`);
                if (destinationCampus !== "")
                    destinationCampus += ", ";
                destinationCampus += campus.campusName;
            }
            catch (error: any) {
                this.setState({ errorMessage: `Unknown LabSim campus was assigned to ${customer.name}. Please save this order, close this window and set a correct LabSim campus in the Customer card.` });
                return { error: true };
            }
        }

        return {
            customerNames: customerNames,
            destinationCampus: destinationCampus
        };
    }


    // Check if this order is ready to submit
    readyToSubmit = (): boolean => {
        return false;
        if (isAccountingPersonnel())
            return false;
        if (this.state.inProgress !== "" || this.state.taxInProgress)  // If we are currently saving or calculating taxes, don't allow a (second) click
            return false;
        let salesOrder = this.state.salesOrder;
        if (salesOrder.status !== SalesOrderStatus.Open)               // If this is a previously submitted order, don't allow a re-submit
            return false;
        if (salesOrder.shippingCustomer === undefined)                  // Customer must be selected
            return false;
        if (salesOrder.items.length === 0)                             // At least 1 sales item must be added
            return false;
        if (salesOrder.shippingCustomer?.creditOnHold)                 // Customer cannot have their credit on hold
            return false;
        if (salesOrder.orderNumber === "")                             // Order has to have been saved first
            return false;
        return true;
    }

    cancelModal = () => { this.setState({ showModal: false, showAttachmentModal: false }); }

    // Do the requested action of Delete, Void, Fraudulent, Return or Submit Order
    submitModal = async () => {
        // Clear any prior errors and set that we are saving
        this.setState({ errorMessage: "", showModal: false, inProgress: `Order ${this.state.modalSubmitButtonText} is being processed...` });
        try {
            if (this.state.modalSubmitButtonText === "Delete") {
                // Delete the draft order
                await Api.delete(`/api/orders/${this.state.salesOrder.orderNumber}`);
                this.setState({ inProgress: "", salesOrder: new SalesOrder() });
                this.closeWindow();
                return;
            }
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while deleting the sales order: ${error.message}`, inProgress: "" });
            return;
        }

        try {
            if (this.state.modalSubmitButtonText === "Submit") {
                // Submit the order
                await Api.post<string>("/api/orders/submit", this.state.salesOrder);
                this.setState({ inProgress: "", salesOrder: new SalesOrder() });
                this.closeWindow();
                return;
            }
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while submitting the sales order: ${error.message}`, inProgress: "" });
            return;
        }

        let salesReturn = new SalesReturn();
        salesReturn.returnDate = new Date();
        // Get the saved version of the sales order from the database (ignore any UI changes that might have happened)
        let savedOrder = new SalesOrder(await Api.get(`/api/orders/${this.state.salesOrder.orderNumber}`));
        salesReturn.salesOrder = this.cleanUpOrder(savedOrder) ?? savedOrder;
        // Set the type of return this is
        if (this.state.modalSubmitButtonText === "Void")
            salesReturn.returnType = SalesReturnType.Void;
        else if (this.state.modalSubmitButtonText === "Fraud")
            salesReturn.returnType = SalesReturnType.Fraud;
        else if (this.state.modalSubmitButtonText === "Return")
            salesReturn.returnType = SalesReturnType.Return;
        else if (this.state.modalSubmitButtonText === "Exchange") {
            salesReturn.returnType = SalesReturnType.Exchange;
            // TODO - Verify that the CSR is actually exchanging something
            if (this.state.exchangeProductNumbers !== undefined) {
                salesReturn.items = this.state.salesOrder.items;
                for (let i = 0; i < salesReturn.items.length; i++) {
                    salesReturn.items[i].productNumber = new ItemNumber(this.state.exchangeProductNumbers[i]);
                }
            }
            if (salesReturn.salesOrder.shippingCustomer.isStudent)
                salesReturn.salesOrder.shippingCustomer.name = salesReturn.salesOrder.shippingCustomer.address.contactPerson.toString();
            if (salesReturn.salesOrder.billingCustomer.isStudent)
                salesReturn.salesOrder.billingCustomer.name = salesReturn.salesOrder.billingCustomer.address.contactPerson.toString();
        }

        try {
            await Api.post<string>("/api/orders/return", salesReturn);
            this.setState({ inProgress: "", salesOrder: new SalesOrder() });
            this.closeWindow();
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while returning the sales order: ${error.message}`, inProgress: "" });
        }
    }

    showDeleteModal = () => {
        this.setState({
            showModal: true,
            modalHeader: "Delete this draft order?",
            modalDescription: "To delete this draft order, click the Delete button. This action is permananent.",
            modalSubmitButtonText: "Delete"
        });
    }

    showVoidModal = () => {
        this.setState({
            showModal: true,
            modalHeader: "Void this order?",
            modalDescription: "To void this order, click the Void button. This order will be marked as voided in the accounting system and a credit will be made to the billing customer for any card payment.",
            modalSubmitButtonText: "Void"
        });
    }

    showFraudModal = () => {
        this.setState({
            showModal: true,
            modalHeader: "Mark this order as fraudulent?",
            modalDescription: "To close this order as fraudulent, click the Fraud button. This order will be marked as fraudelent in the accounting system and a credit will be made to the billing customer for any card payment.",
            modalSubmitButtonText: "Fraud"
        });
    }

    showReturnModal = () => {
        this.setState({
            showModal: true,
            modalHeader: "Return this order?",
            modalDescription: "To return this order, click the Return button. This order will be marked as returned in the accounting system and a credit will be made to the billing customer for any card payment.",
            modalSubmitButtonText: "Return"
        });
    }

    showExchangeModal = () => {
        let exchangeProductNumbers = new Array<string>();
        this.state.salesOrder.items.forEach((item: SalesItem) => { exchangeProductNumbers.push(item.productNumber.toString()) });
        this.setState({
            showModal: true,
            modalHeader: "Exchange items on this order?",
            modalDescription: "To do an exchange, select new products for this order. A new exchange order will be generated containing these new order items, and the current order will be closed. The customer will receive a product de-activated email for all items in the current order, followed by a product activated email for all items in the new order.",
            modalSubmitButtonText: "Exchange",
            exchangeProductNumbers: exchangeProductNumbers
        });
    }

    onExchangeItemChange = (productNumber: string, index: number) => {
        if (this.state.exchangeProductNumbers === undefined)
            return;
        let newExchangeProductNumbers = this.state.exchangeProductNumbers.slice();
        newExchangeProductNumbers[index] = productNumber;
        this.setState({ exchangeProductNumbers: newExchangeProductNumbers });
    }

    closeWindow = () => {
        window.parent.postMessage(JSON.stringify({ action: "CANCEL" }), "*");
    }

    disableExchangeButton = (): boolean => {
        if (this.state.salesOrder.orderNumber.startsWith("OSP") || this.state.salesOrder.orderNumber.startsWith("OCG"))
            return true;
        if (this.state.salesOrder.status !== SalesOrderStatus.Fulfilled && this.state.salesOrder.status !== SalesOrderStatus.Invoiced)
            return true;
        let parentSegmentCode = new Segment(this.state.salesOrder.shippingCustomer.segment).parentSegment?.code || "";
        if (!(this.state.salesOrder.shippingCustomer.isStudent || parentSegmentCode !== Segment.secondaryEd.code))
            return true;
        return isAccountingPersonnel();
    }

    getButtonTitle = (action: string) => {
        return this.state.salesOrder.orderNumber.startsWith("OSP") ? "Shopify orders must be " + action + " in the Shopify admin page" :
               this.state.salesOrder.orderNumber.startsWith("OCG") ? "Chargify orders must be " + action + " in the Maxio admin page" : "";
    }

    getButtonDisabledState = (): boolean => {
        if (this.state.salesOrder.orderNumber.startsWith("OSP") || this.state.salesOrder.orderNumber.startsWith("OCG"))
            return true;
        return (this.state.salesOrder.status !== SalesOrderStatus.Fulfilled &&
                this.state.salesOrder.status !== SalesOrderStatus.Invoiced) || isAccountingPersonnel()
    }

    render() {
        if (!this.state.initialized)
            return <div className="page SalesOrderPage loading"><div className="spinner"></div></div>;

        let salesOrder = this.state.salesOrder;
        let status = salesOrder.status;
        return <div className="page SalesOrderPage">
            {this.state.showModal &&
                <ModalCtl headerText={this.state.modalHeader ?? ""} description={this.state.modalDescription ?? ""} submitButton={this.state.modalSubmitButtonText ?? ""}
                          originalItems={salesOrder.items} saveInProgress={this.state.inProgress !== ""}
                          exchangeProductNumbers={this.state.exchangeProductNumbers} priceList={this.state.salesOrder.pricing?.priceList}
                          onSubmit={this.submitModal} onCancel={this.cancelModal} onChange={this.onExchangeItemChange} />
            }
            {this.state.showAttachmentModal && <AttachmentDlgCtl onClose={this.cancelModal} orderNumber={salesOrder.orderNumber} />}
            <header>
                <div className="title">
                    <h3>{salesOrder.orderNumber === "" ? this.NEW_ORDER : salesOrder.orderNumber}</h3>
                    <h6>{SalesOrderStatus[status]}</h6>
                </div>
                <div className="actions">
                    <button title={this.getButtonTitle("voided")} className="tertiary" disabled={this.getButtonDisabledState()}
                            onClick={() => this.showVoidModal()}>Void</button>
                    {/*<button className="tertiary" disabled={status !== SalesOrderStatus.Fulfilled && status !== SalesOrderStatus.Invoiced}*/}
                    {/*        onClick={() => this.showFraudModal()}>Fraud</button>*/}
                    <button title={this.getButtonTitle("returned")} className="tertiary" disabled={this.getButtonDisabledState()}
                            onClick={() => this.showReturnModal()}>Return</button>
                    <button className="tertiary" disabled={this.disableExchangeButton()} onClick={() => this.showExchangeModal()}>Exchange</button>
                    <button className="primary" disabled={!this.readyToSubmit()} onClick={() => this.submitSalesOrder()}>Submit</button>
                </div>
                <OwnerCtl value={salesOrder.salespersonCode} territory={this.state.territory} employees={this.state.employees} onChange={this.onSalespersonChange} />
                {this.state.errorMessage !== "" && <p className="error" dangerouslySetInnerHTML={{__html: this.state.errorMessage?.replace(/(?:\[(.+)\]\((http.+)\))/, (match, title, url) => `<a href="${url}" target="new" class="simple">${title}</a>`)}} />}
                {salesOrder.status === SalesOrderStatus.Open && !salesOrder.useAutomatedFulfillment &&
                    <p className="warning">Order will need to be manually fulfilled after it is submitted</p>}
            </header>

            <main>
                <section className="common">
                    {/*<LabelCtl label="PO Number">*/}
                    {/*    <TextCtl value={salesOrder.purchaseOrderNumber} onChange={this.onPurchaseOrderNumberChange} disabled={status !== SalesOrderStatus.Open} />*/}
                    {/*</LabelCtl>*/}
                    <LabelCtl label="Order Date">
                        <DateCtl value={salesOrder.orderDate} onChange={this.onOrderDateChange} disabled={status !== SalesOrderStatus.Open} />
                    </LabelCtl>
                    <CheckCtl className="manual" label="Order is fulfilled automatically" value={salesOrder.useAutomatedFulfillment}
                              onChange={this.onAutomatedFulfillmentChange} disabled={status !== SalesOrderStatus.Open} />
                </section>

                <section className="delivery">
                    <h3>Delivery</h3>
                    <CustomerCtl customer={salesOrder.shippingCustomer ?? new Customer({ entityId: Guid.createEmpty(), address: { addressCode: "SHIPPING" } })}
                                 onChange={this.onShippingCustomerChange} hsCompanyId={this.state.hsCompanyId}
                                 disabled={status !== SalesOrderStatus.Open} isRequired={true} onSetErrorMessage={this.showError} />
                    <label><span>Pricing</span><span className="required">&nbsp;*</span>
                        <SelectCtl value={(salesOrder.priceId ?? 232).toString(10)} onChange={this.onPriceIdChange} hideItemValue={false}
                                   errorMessage={salesOrder.shippingCustomer === undefined ? "Please select a customer" : ""}
                                   items={this.state.priceLists ?? new Array<ListItem>()} disabled={status !== SalesOrderStatus.Open} />
                    </label>
                </section>

                <section className="billing">
                    <CheckCtl className="check-right" label="Same as Delivery" value={salesOrder.billingSameAsShipping}
                              onChange={this.onBillingSameAsShippingChange} disabled={status !== SalesOrderStatus.Open} />
                    <h3>Billing</h3>
                    <CustomerCtl customer={salesOrder.billingCustomer ?? new Customer({ entityId: Guid.createEmpty(), address: { addressCode: "BILLING" } })}
                                 onChange={this.onBillingCustomerChange} hsCompanyId={this.state.hsCompanyId}
                                 disabled={salesOrder.billingSameAsShipping || status !== SalesOrderStatus.Open} />
                    <PaymentCtl disabled={status !== SalesOrderStatus.Open} paymentTerms={salesOrder.paymentTerms ?? PaymentTerms.Cash.toString()}
                                payment={salesOrder.payments[0]} onChange={this.onPaymentChange}
                                purchaseOrderNumber={salesOrder.purchaseOrderNumber} onPurchaseOrderNumberChange={this.onPurchaseOrderNumberChange} />
                </section>

                <section className="products">
                    <LineItemsCtl salesBase={salesOrder} childrenCustomers={salesOrder.shippingCustomer.isStudent ? {} : this.state.childrenCustomers}
                                  updateOrderOrQuote={this.setStateSalesOrder} showError={this.showError}
                                  disabled={status !== SalesOrderStatus.Open} checkingTaxes={this.checkingTaxes} />
                </section>

                <section className="comments">
                    <LabelCtl label="Comments"><TextCtl type="textarea" value={salesOrder.comments} onChange={this.onCommentsChange} disabled={status !== SalesOrderStatus.Open} /></LabelCtl>
                </section>

                <section className="summary">
                    <div id="LineItemTotal"><span>Subtotal</span><span>{salesOrder.totalItemAmount.toLocaleString("en-US", { style: "currency", currency: "USD" })}</span></div>
                    <div id="DiscountTotal"><span>Discount</span><span>{salesOrder.totalDiscountAmount.toLocaleString("en-US", { style: "currency", currency: "USD" })}</span></div>
                    <div id="FrieghtTotal"><span>Freight</span><span>{salesOrder.freightAmount.toLocaleString("en-US", { style: "currency", currency: "USD" })}</span></div>
                    <div id="SalesTax"><span>Sales Tax</span><span>{this.state.taxInProgress && <div className="tax spinner"></div>}{salesOrder.totalTaxAmount.toLocaleString("en-US", { style: "currency", currency: "USD" })}</span></div>
                    <div id="Total"><span>Total</span><span>{salesOrder.totalAmount.toLocaleString("en-US", { style: "currency", currency: "USD" })}</span></div>
                </section>
            </main>

            {this.state.inProgress !== "" &&
                <div className="saving"><div><div>{this.state.inProgress}</div><div className="spinner"></div></div></div>}

            <footer className="orderFooter">
                {/*<button className="secondary">Teachers</button>*/}
                <button className="tertiary" disabled={salesOrder.orderNumber === "" || status !== SalesOrderStatus.Open || isAccountingPersonnel()}
                        onClick={() => this.showDeleteModal()}>Delete</button>
                <button className="secondary" disabled={salesOrder.orderNumber === ""} onClick={() => this.setState({ showAttachmentModal: true })}>Attachments</button>
                <button className="secondary" onClick={() => this.closeWindow()}>Close</button>
                <button disabled={(salesOrder.shippingCustomer?.isStudent === false && salesOrder.shippingCustomer?.customerNumber === "") ||
                                  (salesOrder.shippingCustomer?.isStudent === true && salesOrder.shippingCustomer?.parentCustomerNumber === "") ||
                                  isAccountingPersonnel() || status !== SalesOrderStatus.Open || this.state.taxInProgress || this.state.inProgress !== ""}
                        className="primary" onClick={() => this.saveSalesOrder()}>Save</button>
            </footer>

        </div>;
    }
}
